import { Box, Grid, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumberFormatValues, NumericFormat } from "react-number-format";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import InfoTooltip from "../../../../../common/components/functional/InfoTooltip/InfoTooltip";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../../../common/helpers/isEmpty";
import { changeAssignmentInvPrice } from "../../../../talents/talentsSlice";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import { Input } from "../../../editForms/salaries/CustomInputs";
import { SalaryType } from "../../../../orders/types/salaryType";

type Props = {
  canEdit: boolean;
};

const AssignmentBasedPricing = ({ canEdit }: Props) => {
  const [edit, setEdit] = useState<boolean>(false);

  const assignment = useAppSelector((state) => state.talents.assignment);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { canReadAssignmentPricingInfo } = useContext(AccessRightContext);
  const canRead = canReadAssignmentPricingInfo(assignment.eezyCompanyId);

  const defaultValues = {
    invoicingPriceOverride: assignment.details.invoicingPriceOverride || "",
  };

  const methods = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
  });
  const isMonthlySalary =
    assignment.employmentSalaryType === SalaryType.Monthly;

  useEffect(() => {
    // We may render this component before we have the data to initialize the form with defaultValues.
    // Reset form if we get actual values later.
    methods.reset({
      invoicingPriceOverride: assignment.details.invoicingPriceOverride || "",
    });
  }, [assignment.details.invoicingPriceOverride, methods]);

  if (!canRead) return null;

  const onSubmit = (data: FieldValues) => {
    const updatedUnitPrice = isEmpty(data.invoicingPriceOverride)
      ? null
      : data.invoicingPriceOverride;

    const payload = {
      assignmentId: assignment.assignmentId,
      version: assignment.details.version,
      unitPrice: updatedUnitPrice,
    };

    dispatch(changeAssignmentInvPrice(payload)).then((action) => {
      if (isFulfilled(action)) {
        setEdit(false);
      }
    });
  };

  const onEdit = () => {
    if (!edit) {
      setEdit(true);
    }
    if (edit) return methods.handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    methods.reset();
    setEdit(false);
  };

  const isAllowed = (values: NumberFormatValues) => {
    if (values.floatValue && values.floatValue > 999.99) return false;
    return true;
  };

  const title = isMonthlySalary
    ? t("order:billMonthlySalaryAsHourlyTitle")
    : t("talentProfile:assignmentBasedPricing");
  const Tooltip = (
    <Box display="flex" alignItems="center" marginLeft="13px">
      <InfoTooltip
        title={
          isMonthlySalary
            ? t("order:billMonthlySalaryAsHourlyGuideText")
            : t(`talentProfile:assignmentBasedPricingTooltip`)
        }
      />
    </Box>
  );

  return (
    <FormSection
      title={title}
      infoTooltip={Tooltip}
      onEdit={onEdit}
      edit={edit}
      onCancel={onCancel}
      saveDisabled={!methods.formState.isDirty}
      readOnly={!canEdit}
    >
      <FormProvider {...methods}>
        <Grid item textAlign="end" alignSelf="center" xs={2}>
          <Typography variant="secondary">
            {t("talentProfile:pricing")}
          </Typography>
        </Grid>
        <Grid container item xs={4}>
          <Box display="flex" alignItems="center" gap="22px">
            <Controller
              name="invoicingPriceOverride"
              control={methods.control}
              render={({ field }) => (
                <NumericFormat
                  disabled={!edit}
                  isAllowed={(values) => isAllowed(values)}
                  value={field.value}
                  decimalSeparator=","
                  valueIsNumericString
                  onValueChange={(values) => {
                    field.onChange(values.value);
                  }}
                  maxwidth="135px"
                  customInput={Input}
                  decimalScale={2}
                  unit="€/h"
                  allowNegative={false}
                />
              )}
            />
          </Box>
        </Grid>
      </FormProvider>
    </FormSection>
  );
};

export default AssignmentBasedPricing;
